// Phase 9 Plan 03 Task 1 — Iterative Predicate walker.
//
// Mirrors the iterative discipline of `countPredicateNodes` /
// `checkPredicateDepthPrewalk` in `packages/shared/src/schemas/constraint.ts`
// (lines 162-228) — explicit worklist, NEVER recursive. The recipe Constraint AST
// is bounded ≤ 8 deep by Zod, but the walker enforces its own defense-in-depth
// against synthesized payloads that bypass Zod.
//
// Two-phase post-order walk:
//   1. Stack frames carry (node, depth). For unvisited nodes, push them; for
//      group nodes whose children are all visited, fold via Map<Predicate, boolean>.
//   2. Leaf nodes evaluate to boolean directly via evaluateLeaf().
//
// Self-reference detection (mentioned in RESEARCH §predicate.ts): v1 leaf
// grammar (batch-set-empty / batch-set-position / spread-recipe-count /
// other-batch-sets-all-empty / active-mode-id) carries NO widgetInstanceId
// reference (Q-04). True selectability self-reference is impossible until the
// leaf grammar widens — the walker's "self-reference" reason is reserved for
// future use when the grammar carries widget refs.
//
// Bounds:
//   MAX_NODES = 100 (matches constraint.ts countPredicateNodes cap)
//   MAX_DEPTH = 8   (matches constraint.ts superRefine depth cap)
//
// PARANOID-STRICT compliance: explicit return types, exhaustive switch with
// `_exhaustive: never` compile-time check, no unchecked indexed access.

import type { Predicate } from "../schemas/constraint.js";
import type { EvalContext, PredicateResult } from "./types.js";

const MAX_NODES = 100;
const MAX_DEPTH = 8;

// Discriminated narrowing helper — extracts the leaf branch of the Predicate union
// for evaluateLeaf's parameter type without using `Extract<>` at the call site.
type LeafPredicate = Extract<Predicate, { kind: "leaf" }>;

/**
 * Iterative explicit-stack walker. Returns {ok:true, value} on successful
 * evaluation; {ok:false, reason, nodePath} on structural rejection.
 *
 * NEVER calls itself recursively (gate enforced via grep in plan verification).
 * The execution loop is a single while-loop over a stack of {node, depth} frames.
 *
 * @param root - the Predicate AST to evaluate (bounded ≤ 8 deep, ≤ 100 nodes)
 * @param context - read-only spread/recipe state
 */
export function walkPredicate(root: Predicate, context: EvalContext): PredicateResult {
  // Defensive root null-check — Zod parse already rejects non-object inputs, but
  // the walker is reachable from places that may bypass Zod (e.g. test synthesis).
  if (root === null || typeof root !== "object" || !("kind" in root)) {
    return { ok: false, reason: "unknown-variable", nodePath: "root" };
  }

  const visited = new Map<Predicate, boolean>();
  const stack: Array<{ node: Predicate; depth: number }> = [{ node: root, depth: 0 }];
  let processed = 0;

  while (stack.length > 0) {
    // noUncheckedIndexedAccess — array[i] is T | undefined; defensive narrow.
    const top = stack[stack.length - 1];
    if (top === undefined) break;
    const { node, depth } = top;

    // Already visited — pop and continue (we'll re-process the parent next iteration).
    if (visited.has(node)) {
      stack.pop();
      continue;
    }

    // MAX_NODES self-limit — defense-in-depth against bypassed Zod parse.
    if (processed >= MAX_NODES) {
      return { ok: false, reason: "depth-exceeded", nodePath: `processed=${processed}` };
    }

    // Depth cap — matches the constraint.ts superRefine bound.
    if (depth > MAX_DEPTH) {
      return { ok: false, reason: "depth-exceeded", nodePath: `depth=${depth}` };
    }

    if (node.kind === "leaf") {
      // Leaf — evaluate directly and fold up.
      const value = evaluateLeaf(node, context);
      visited.set(node, value);
      stack.pop();
      processed++;
      continue;
    }

    // Group node — and / or. Fold up only after all children are visited.
    const allChildrenVisited = node.predicates.every((child) => visited.has(child));
    if (allChildrenVisited) {
      // noUncheckedIndexedAccess: visited.get may be undefined; we just verified
      // every child IS in visited, so `?? false` is safe defensive narrowing.
      const childResults = node.predicates.map((child) => visited.get(child) ?? false);
      const value =
        node.kind === "and"
          ? childResults.every((v) => v === true)
          : childResults.some((v) => v === true);
      visited.set(node, value);
      stack.pop();
      processed++;
      continue;
    }

    // Push unvisited children. We push ALL unvisited children at depth+1; the
    // top-of-stack loop will visit each one. When all are folded, the parent's
    // `allChildrenVisited` branch above fires and computes the group value.
    for (const child of node.predicates) {
      if (!visited.has(child)) {
        stack.push({ node: child, depth: depth + 1 });
      }
    }
  }

  const rootValue = visited.get(root);
  if (rootValue === undefined) {
    // Reached if the walker exited the loop without folding the root — this
    // would only happen on a malformed input the defensive checks didn't catch.
    return { ok: false, reason: "unknown-variable", nodePath: "root" };
  }
  return { ok: true, value: rootValue };
}

/**
 * Evaluate a leaf Predicate against the EvalContext.
 *
 * Exhaustiveness is enforced at compile time via `const _exhaustive: never = leaf`
 * in the default branch — adding a 4th leaf-variable family without updating this
 * dispatcher fails the typecheck.
 */
function evaluateLeaf(leaf: LeafPredicate, context: EvalContext): boolean {
  switch (leaf.variable) {
    case "batch-set-empty":
      // "empty" = the per-widget state map for the current batch set has no entries.
      return matchBool(leaf.operator, context.widgetStates.size === 0);
    case "other-batch-sets-all-empty":
      return matchBool(leaf.operator, context.otherBatchSetsAllEmpty);
    case "batch-set-position":
      return matchNumber(leaf.operator, context.currentBatchSetIndex, leaf.value);
    case "spread-recipe-count":
      return matchNumber(leaf.operator, context.spreadRecipeCount, leaf.value);
    case "active-mode-id":
      // undefined → "" (so "eq plan" against missing mode returns false).
      return matchString(leaf.operator, context.activeModeId ?? "", leaf.value);
    default: {
      // Compile-time exhaustiveness — if the Predicate leaf-variable union widens,
      // this assignment fails typecheck and forces the dispatcher to update.
      const _exhaustive: never = leaf;
      void _exhaustive;
      return false;
    }
  }
}

function matchBool(op: "is-true" | "is-false", actual: boolean): boolean {
  return op === "is-true" ? actual : !actual;
}

function matchNumber(
  op: "eq" | "ne" | "lt" | "lte" | "gt" | "gte",
  actual: number,
  expected: number,
): boolean {
  switch (op) {
    case "eq":
      return actual === expected;
    case "ne":
      return actual !== expected;
    case "lt":
      return actual < expected;
    case "lte":
      return actual <= expected;
    case "gt":
      return actual > expected;
    case "gte":
      return actual >= expected;
    default: {
      const _exhaustive: never = op;
      void _exhaustive;
      return false;
    }
  }
}

function matchString(op: "eq" | "ne", actual: string, expected: string): boolean {
  return op === "eq" ? actual === expected : actual !== expected;
}
